import React, { useState } from 'react'
import { DietCount, MenuConcept, MenuItem } from './types'

interface MenuTableProps {
  isLoading: boolean
  menus: MenuConcept[]

  renderRow: (menu: MenuConcept) => React.ReactNode
  renderStagingMenusFinalRow?: () => React.ReactNode
}

export const MenuTable = ({
  isLoading,
  menus,
  renderRow,
  renderStagingMenusFinalRow,
}: MenuTableProps) => {
  return (
    <div className="overflow-x-auto">
      <div className="align-middle inline-block min-w-full">
        <div className="shadow-md overflow-auto border border-gray-300 border-solid sm:rounded-lg">
          <table className="min-w-full">
            <thead className="bg-teal-500">
              <tr>
                <th
                  scope="col"
                  className="px-4 py-3 text-left font-semibold text-white uppercase tracking-wider"
                >
                  Name
                </th>
                <th
                  scope="col"
                  className="px-4 py-3 text-left font-semibold text-white uppercase tracking-wider"
                >
                  Chef
                </th>
                <th
                  scope="col"
                  className="px-4 py-3 text-left font-semibold text-white uppercase tracking-wider"
                >
                  Cuisines
                </th>
                <th
                  scope="col"
                  className="px-4 py-3 text-left font-semibold text-white uppercase tracking-wider"
                >
                  Has Mods
                </th>
                <th
                  scope="col"
                  className="px-4 py-3 text-left font-semibold text-white uppercase tracking-wider"
                >
                  Available Days
                </th>
                <th
                  scope="col"
                  className="px-4 py-3 text-left font-semibold text-white uppercase tracking-wider"
                >
                  Cutoff
                </th>
                <th
                  scope="col"
                  className="px-4 py-3 text-left font-semibold text-white uppercase tracking-wider"
                >
                  Earliest Pickup
                </th>
                <th
                  scope="col"
                  className="px-4 py-3 text-left font-semibold text-white uppercase tracking-wider"
                >
                  Max Items
                </th>
                <th
                  scope="col"
                  className="px-4 py-3 text-left font-semibold text-white uppercase tracking-wider"
                >
                  Item Count
                </th>
                <th
                  scope="col"
                  className="px-4 py-3 text-left font-semibold text-white uppercase tracking-wider"
                >
                  Min Payout
                </th>
                <th
                  scope="col"
                  className="px-4 py-3 text-left font-semibold text-white uppercase tracking-wider"
                >
                  Dietary Counts
                </th>
              </tr>
            </thead>
            <tbody className="bg-white">
              {isLoading && (
                <tr>
                  <td colSpan={11} className="text-center py-4">
                    Loading...
                  </td>
                </tr>
              )}
              {!isLoading && menus.length === 0 && (
                <tr>
                  <td colSpan={11} className="text-center py-4 text-gray-900">
                    No menus found.
                  </td>
                </tr>
              )}
              {!isLoading &&
                menus.length > 0 &&
                menus.map((m) => (
                  <React.Fragment key={m.id}>{renderRow(m)}</React.Fragment>
                ))}
              {renderStagingMenusFinalRow && renderStagingMenusFinalRow()}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  )
}

export const MenuItemsSubtable: React.FC<{ items: MenuItem[] }> = ({
  items,
}) => {
  const [expandedDescriptions, setExpandedDescriptions] = useState<Set<string>>(
    new Set(),
  )

  const toggleDescription = (itemId: string) => {
    setExpandedDescriptions((prev) => {
      const next = new Set(prev)
      if (next.has(itemId)) {
        next.delete(itemId)
      } else {
        next.add(itemId)
      }

      return next
    })
  }

  if (!items?.length) {
    return <div className="text-sm text-gray-500">No items.</div>
  }

  return (
    <div className="overflow-x-auto rounded-lg border-2 border-solid border-gray-300 shadow-sm">
      <table className="min-w-full table-fixed">
        <thead className="bg-gradient-to-r from-teal-200 to-blue-200">
          <tr>
            <th className="w-[200px] px-4 py-3 text-left text-xs font-bold text-gray-800 uppercase tracking-wider border-r border-gray-300">
              Name
            </th>
            <th className="w-[120px] px-4 py-3 text-left text-xs font-bold text-gray-800 uppercase tracking-wider border-r border-gray-300">
              Type
            </th>
            <th className="w-[100px] px-4 py-3 text-left text-xs font-bold text-gray-800 uppercase tracking-wider border-r border-gray-300">
              Price
            </th>
            <th className="w-[100px] px-4 py-3 text-left text-xs font-bold text-gray-800 uppercase tracking-wider border-r border-gray-300">
              Chef Price
            </th>
            <th className="w-[400px] px-4 py-3 text-left text-xs font-bold text-gray-800 uppercase tracking-wider">
              Description
            </th>
          </tr>
        </thead>
        <tbody className="bg-white divide-y divide-gray-200">
          {items.map((it, index) => {
            const isExpanded = expandedDescriptions.has(it.id)
            const hasLongDescription =
              it.description && it.description.length > 100
            let { description } = it
            if (hasLongDescription && !isExpanded) {
              description = it.description.slice(0, 50) + '...'
            }

            return (
              <tr
                key={it.id}
                className={`transition-colors hover:bg-blue-50 ${
                  index % 2 === 0 ? 'bg-white' : 'bg-gray-50'
                }`}
              >
                <td className="px-4 py-3 text-sm font-medium text-gray-900 border-r-2 border-solid border-gray-200">
                  {it.name}
                </td>
                <td className="px-4 py-3 text-sm text-gray-700 border-r border-gray-200">
                  {it.itemType ? (
                    <span className="inline-block px-2 py-1 text-xs font-semibold rounded-full bg-purple-100 text-purple-800">
                      {it.itemType}
                    </span>
                  ) : (
                    <span className="text-gray-400">—</span>
                  )}
                </td>
                <td className="px-4 py-3 text-sm font-semibold text-green-700 border-r-2 border-solid border-gray-200">
                  ${(it.price ?? 0).toFixed(2)}
                </td>
                <td className="px-4 py-3 text-sm font-semibold text-blue-700 border-r-2 border-solid border-gray-200">
                  ${(it.chefPrice ?? 0).toFixed(2)}
                </td>
                <td className="px-4 py-3 text-sm text-gray-600">
                  {it.description ? (
                    <div className="flex flex-col gap-1">
                      <p>{description}</p>
                      {hasLongDescription && (
                        <button
                          onClick={() => toggleDescription(it.id)}
                          className="text-xs font-semibold text-blue-600 hover:text-blue-800 underline self-start focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-1 rounded px-1"
                        >
                          {isExpanded ? 'Show less' : 'Show more'}
                        </button>
                      )}
                    </div>
                  ) : (
                    <span className="text-gray-400 italic">No description</span>
                  )}
                </td>
              </tr>
            )
          })}
        </tbody>
      </table>
    </div>
  )
}

interface DietCountsCellProps {
  tags: DietCount[]
  showAllTags: boolean
  topNumber?: number
}

export const DietCountsCell = ({
  tags,
  showAllTags,
  topNumber = 2,
}: DietCountsCellProps) => {
  const [open, setOpen] = useState(false)

  if (!tags?.length) {
    return <span className="text-gray-400">—</span>
  }

  const sorted = [...tags].sort(
    (a, b) => b.count - a.count || a.tag.localeCompare(b.tag),
  )
  const visible = showAllTags ? sorted : sorted.slice(0, topNumber)
  const hidden = sorted.slice(topNumber)
  const hiddenCount = hidden.length

  return (
    <div className="relative flex flex-col items-center gap-1">
      {visible.map((t) => (
        <span
          key={t.tag}
          className="px-2 py-1 rounded-md bg-blue-200 whitespace-nowrap"
        >
          {t.tag} ({t.count})
        </span>
      ))}

      {hiddenCount > 0 && !showAllTags && (
        <>
          <button
            aria-expanded={open}
            onClick={() => setOpen((v) => !v)}
            onMouseEnter={() => setOpen(true)}
            onMouseLeave={() => setOpen(false)}
            onBlur={() => setOpen(false)}
            className="px-2 py-1 rounded-md bg-gray-400 text-blue-800 hover:bg-gray-300 focus:outline-none focus:ring-2 focus:ring-teal-400"
            title={hidden.map((h) => `${h.tag} (${h.count})`).join(', ')}
          >
            +{hiddenCount} more
          </button>

          {open && (
            <div
              onMouseEnter={() => setOpen(true)}
              onMouseLeave={() => setOpen(false)}
              className="absolute z-20 top-full mt-1 left-0 w-32  rounded-md border bg-white shadow-md p-2"
              role="dialog"
            >
              <div
                className="grid grid-cols-1 gap-1 overflow-auto"
                style={{ maxHeight: '100px' }}
              >
                {hidden.map((t) => (
                  <div
                    key={t.tag}
                    className="px-2 py-1 rounded bg-gray-100 text-gray-900"
                  >
                    {t.tag} ({t.count})
                  </div>
                ))}
              </div>
            </div>
          )}
        </>
      )}
    </div>
  )
}
